import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

import type { DataTableInstance } from "../../table-features";
import type { TextFilterField, TextFilterOperator } from "../types";

import { cn } from "../../../../../../utils/shadcn-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../base/popover";
import { resolveEnumLabel } from "../../../../fields/utils";
import { getOperator, setOperator } from "../tanstack-bridge";

const OPERATOR_LABELS: Record<TextFilterOperator, MessageDescriptor> = {
  contains: msg`Contains`,
  equals: msg`Equals`,
  starts_with: msg`Starts with`,
};

export function OperatorSelect<T>({
  table,
  field,
}: {
  table: DataTableInstance<T>;
  field: TextFilterField<T>;
}) {
  const operator = getOperator(table, field);
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md border border-input bg-transparent text-muted-foreground hover:border-brand-primary hover:text-brand-primary cursor-pointer whitespace-nowrap shrink-0"
        >
          {resolveEnumLabel(OPERATOR_LABELS[operator])}
          <ChevronDown size={10} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-1 w-36 rounded-md">
        {(
          Object.entries(OPERATOR_LABELS) as [
            TextFilterOperator,
            MessageDescriptor,
          ][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setOperator(table, field, key);
              setOpen(false);
            }}
            className={cn(
              "w-full flex items-center px-2 py-1.5 text-xs rounded-md text-left bg-transparent border-0 hover:bg-muted cursor-pointer",
              operator === key && "text-brand-primary font-semibold",
            )}
          >
            {resolveEnumLabel(label)}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
